import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '@/lib/store';
import { generate, payment } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';

export function CheckoutPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { selectedVersion, formData } = useStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<'standard' | 'premium'>('standard');
  const [customSubdomain, setCustomSubdomain] = useState('');
  const [sessionData, setSessionData] = useState<{ inputData: { businessName: string } } | null>(null);

  useEffect(() => {
    if (!sessionId || !selectedVersion) {
      navigate('/generate');
      return;
    }

    loadSessionData();
  }, [sessionId, selectedVersion]);

  const loadSessionData = async () => {
    try {
      const session = await generate.get(sessionId!);
      setSessionData({ inputData: session.inputData });
    } catch (err) {
      console.error('Failed to load session data:', err);
    }
  };

  const handleCheckout = async () => {
    if (!sessionId || !selectedVersion) return;

    setLoading(true);
    setError(null);

    try {
      const result = await payment.createSession(
        sessionId,
        selectedVersion,
        plan,
        plan === 'premium' ? customSubdomain : undefined
      );

      // Redirect to Stripe checkout
      window.location.href = result.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create checkout session');
      setLoading(false);
    }
  };

  const businessName = sessionData?.inputData?.businessName || formData.businessName || 'Your Business';

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Order</h1>
          <p className="text-gray-600">
            You're almost there! Review your order and complete payment.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Order Summary */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>

            <div className="border-b pb-4 mb-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">Landing Page for {businessName}</h3>
                  <p className="text-sm text-gray-500">Version {selectedVersion?.replace('v', '')} selected</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">One-time design fee</span>
                <span className="font-medium">$50.00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly hosting</span>
                <span className="font-medium">$5.00/mo</span>
              </div>
              {plan === 'premium' && (
                <div className="flex justify-between text-primary-600">
                  <span>Custom subdomain</span>
                  <span className="font-medium">+$5.00/mo</span>
                </div>
              )}
            </div>

            <div className="border-t mt-4 pt-4">
              <div className="flex justify-between text-lg font-semibold">
                <span>Today's total</span>
                <span>$55.00</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Then ${plan === 'premium' ? '10' : '5'}/month starting next month
              </p>
            </div>
          </div>

          {/* Plan Selection */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Choose Your Plan</h2>

            <div className="space-y-3">
              <label
                className={`
                  flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all
                  ${plan === 'standard' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}
                `}
              >
                <input
                  type="radio"
                  name="plan"
                  value="standard"
                  checked={plan === 'standard'}
                  onChange={() => setPlan('standard')}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-900">Standard</span>
                    <span className="font-medium text-gray-900">$5/mo</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Auto-generated subdomain (e.g., acme-abc123.pages.dev)
                  </p>
                </div>
              </label>

              <label
                className={`
                  flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all
                  ${plan === 'premium' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}
                `}
              >
                <input
                  type="radio"
                  name="plan"
                  value="premium"
                  checked={plan === 'premium'}
                  onChange={() => setPlan('premium')}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-900">Premium</span>
                    <span className="font-medium text-gray-900">$10/mo</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose your own subdomain (e.g., yourbrand.zlandr.com)
                  </p>
                </div>
              </label>
            </div>

            {plan === 'premium' && (
              <div className="mt-4 pt-4 border-t">
                <label className="label">Your Custom Subdomain</label>
                <div className="flex items-center">
                  <input
                    type="text"
                    className="input rounded-r-none"
                    placeholder="yourbrand"
                    value={customSubdomain}
                    onChange={(e) => setCustomSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  />
                  <span className="bg-gray-100 border border-l-0 border-gray-300 px-4 py-3 rounded-r-lg text-gray-500">
                    .zlandr.com
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Only lowercase letters, numbers, and hyphens allowed.
                </p>
              </div>
            )}
          </div>

          {/* What's Included */}
          <div className="card bg-green-50 border-green-200">
            <h3 className="font-semibold text-green-800 mb-3">What's Included</h3>
            <ul className="space-y-2 text-sm text-green-700">
              {[
                'Instant deployment to global CDN',
                'Free SSL certificate',
                'Contact form with email notifications',
                'Analytics dashboard',
                'Unlimited page regenerations',
                'Mobile responsive design',
              ].map((item) => (
                <li key={item} className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={loading || (plan === 'premium' && !customSubdomain)}
            className="btn-primary w-full text-lg py-4"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2 text-white" />
                Redirecting to payment...
              </>
            ) : (
              <>
                Complete Payment - $55.00
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </>
            )}
          </button>

          <p className="text-center text-sm text-gray-500">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
